import React from "react";

/**
 * /oracle/platform-map — Advanced public page that surfaces the technical
 * complexity (routes, providers, countries, schedulers, architecture) that
 * was previously bleeding into the homepage. Public-safe content only.
 * NO Mongo / LLM / provider key diagnostics — those live under /adminone.
 */
const C = { bg: "#0a1a14", emerald: "#10b981", gold: "#facc15", ivory: "#f5f5dc", ivoryD: "#d4d4aa", border: "rgba(16,185,129,0.25)" };
const S = {
  page: { minHeight: "100vh", background: C.bg, color: C.ivory, padding: "28px 20px 80px", fontFamily: "'Inter', system-ui, sans-serif" },
  inner: { maxWidth: 1100, margin: "0 auto" },
  h1: { fontFamily: "'Cinzel', serif", color: C.gold, fontSize: "clamp(26px, 4vw, 38px)", margin: 0, letterSpacing: 1 },
  kicker: { textTransform: "uppercase", color: C.emerald, fontSize: 11, letterSpacing: 3, fontWeight: 700 },
  lead: { color: C.ivoryD, fontSize: 15, lineHeight: 1.6, marginTop: 6, maxWidth: 760 },
  sec: { marginTop: 36, paddingTop: 18, borderTop: `1px solid ${C.border}` },
  h2: { color: C.gold, fontSize: 18, margin: "0 0 12px", letterSpacing: 1 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 },
  card: { background: "rgba(16,185,129,0.04)", border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", fontSize: 13, color: C.ivoryD, lineHeight: 1.4 },
  cardLabel: { color: C.ivory, fontWeight: 700, fontSize: 13, marginBottom: 2 },
};

const ROUTES = [
  "/", "/oracle/health", "/oracle/capital", "/oracle/market", "/oracle/grants",
  "/oracle/research", "/oracle/lifescore", "/oracle/foodrx", "/oracle/funding",
  "/oracle/predictive", "/oracle/core-satellite", "/logix2", "/lifecommand",
  "/intelligence-globe", "/access", "/oracle/platform-map",
];
const COUNTRIES = [
  { code: "NG", name: "Nigeria" }, { code: "GH", name: "Ghana" }, { code: "KE", name: "Kenya" },
  { code: "RW", name: "Rwanda" }, { code: "ZA", name: "South Africa" }, { code: "EG", name: "Egypt" },
  { code: "MA", name: "Morocco" }, { code: "ET", name: "Ethiopia" }, { code: "SN", name: "Senegal" },
  { code: "CI", name: "Côte d'Ivoire" },
];
const REGIONS = ["North America", "Europe", "Asia-Pacific", "Middle East", "Africa", "Emerging Markets"];

export default function OraclePlatformMapPage() {
  return (
    <main style={S.page} data-testid="oracle-platform-map-page">
      <div style={S.inner}>
        <div style={S.kicker}>CareLogix ORACLE™ · Advanced</div>
        <h1 style={S.h1}>Platform Map</h1>
        <p style={S.lead}>
          A high-level map of the public ORACLE intelligence surface. For operational telemetry
          and provider diagnostics, AdminOne operators use the System Readiness console.
        </p>

        <section style={S.sec} data-testid="pm-sec-architecture">
          <h2 style={S.h2}>Core-Satellite Architecture</h2>
          <div style={S.grid}>
            <div style={S.card}><div style={S.cardLabel}>Core (always-on)</div>LOGIX-2™ · LifeScore™ · FoodRx™ · CareThreads™ · Membership Engine · Trust &amp; Safety · Knowledge Mesh™ · Capital Intelligence</div>
            <div style={S.card}><div style={S.cardLabel}>Health Satellites</div>Lab orders · Prescription continuity · Non-emergency transport · DME supply · In-home companion · Field technician dispatch · Remote patient monitoring</div>
            <div style={S.card}><div style={S.cardLabel}>Executive Satellites</div>CEO Travel · Funding Intel · Grants Command · Africa Health Mesh · Executive Intelligence</div>
            <div style={S.card}><div style={S.cardLabel}>Capital Satellites</div>Capital Command · Market Intelligence · Portfolio Builder · Risk Intelligence</div>
            <div style={S.card}><div style={S.cardLabel}>Future Satellites</div>Legal · Real Estate · Insurance · Government Contracts · Mergers &amp; Acquisitions</div>
          </div>
        </section>

        <section style={S.sec} data-testid="pm-sec-routes">
          <h2 style={S.h2}>Public Routes</h2>
          <div style={S.grid}>
            {ROUTES.map((r) => <div key={r} style={S.card}><code style={{ color: C.gold }}>{r}</code></div>)}
          </div>
        </section>

        <section style={S.sec} data-testid="pm-sec-countries">
          <h2 style={S.h2}>Africa Health Mesh — 10 anchor countries</h2>
          <div style={S.grid}>
            {COUNTRIES.map((c) => <div key={c.code} style={S.card}><strong style={{ color: C.ivory }}>{c.code}</strong> {c.name}</div>)}
          </div>
        </section>

        <section style={S.sec} data-testid="pm-sec-regions">
          <h2 style={S.h2}>Global AI Regions</h2>
          <div style={S.grid}>
            {REGIONS.map((r) => <div key={r} style={S.card}>{r}</div>)}
          </div>
        </section>

        <section style={S.sec} data-testid="pm-sec-schedulers">
          <h2 style={S.h2}>Schedulers (public-safe summary)</h2>
          <div style={S.grid}>
            <div style={S.card}>Daily LOGIX-2™ briefing</div>
            <div style={S.card}>Executive briefing (8 AM / 9 AM UTC)</div>
            <div style={S.card}>LifeCommand™ daily plan</div>
            <div style={S.card}>Cross-service ingestion</div>
            <div style={S.card}>Grants/Funding inbox refresh (every 6h)</div>
          </div>
        </section>

        <div style={{ ...S.card, marginTop: 32, textAlign: "center" }}>
          Research and decision-support only. Not financial, medical, or wagering advice. Human confirmation required.
        </div>
      </div>
    </main>
  );
}
